import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from 'src/app/components/elements/toast/toast.service';
import { PackagesService } from '../../services/http-services/Packages/packages.service';
import { ChangeDetectorRef } from '@angular/core';
import { MediaService } from '../../services/http-services/media.service';

/**
 * LinkedIn workshop modal — status flow without deliverable upload:
 * Received → In progress → Complete (status-only).
 */
@Component({
  selector: 'app-workshop-cv-modal',
  templateUrl: './workshop-cv-modal.component.html',
  styleUrls: ['./workshop-cv-modal.component.scss']
})
export class WorkshopCvModalComponent implements OnInit, OnChanges {
  @Input() workshop: any;

  selectedWorkshop: any = null;
  isWorking = false;

  constructor(
    private packagesService: PackagesService,
    private toastService: ToastService,
    public activeModal: NgbActiveModal,
    private cdr: ChangeDetectorRef,
    private mediaService: MediaService
  ) {}

  ngOnInit(): void {
    if (this.workshop) {
      this.selectedWorkshop = this.workshop;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['workshop'] && changes['workshop'].currentValue) {
      this.selectedWorkshop = changes['workshop'].currentValue;
    }
  }

  close(): void {
    this.activeModal.dismiss();
  }

  hasCv(): boolean {
    return !!this.selectedWorkshop?.uploadedLinkedInCV;
  }

  hasUpdatedCv(): boolean {
    return !!this.selectedWorkshop?.processedLinkedInFile;
  }

  isReceived(): boolean {
    return this.selectedWorkshop?.status === 1;
  }

  isInProgress(): boolean {
    return this.selectedWorkshop?.status === 2;
  }

  isLegacyReviewed(): boolean {
    return this.selectedWorkshop?.status === 3;
  }

  isCompleted(): boolean {
    return this.selectedWorkshop?.status === 4 || this.isLegacyReviewed();
  }

  canMarkDone(): boolean {
    return this.isReceived() || this.isInProgress() || this.isLegacyReviewed();
  }

  downloadCv(): void {
    const url = this.selectedWorkshop?.uploadedLinkedInCV;
    if (!url) return;
    this.mediaService.getMediaUrl(url).subscribe((fullUrl) => {
      if (!fullUrl) return;
      const link = document.createElement('a');
      link.href = fullUrl;
      link.setAttribute('download', `LinkedIn_Submission_${this.selectedWorkshop?.name || 'unknown'}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.toastService.showSuccessMessage('LinkedIn submission downloaded successfully.');
    });
  }

  viewCv(): void {
    const url = this.selectedWorkshop?.uploadedLinkedInCV;
    if (!url) return;
    this.mediaService.getMediaUrl(url).subscribe((fullUrl) => {
      if (fullUrl) window.open(fullUrl, '_blank');
    });
  }

  downloadUpdatedCv(): void {
    const url = this.selectedWorkshop?.processedLinkedInFile;
    if (!url) return;
    this.mediaService.getMediaUrl(url).subscribe((fullUrl) => {
      if (!fullUrl) return;
      const link = document.createElement('a');
      link.href = fullUrl;
      link.setAttribute('download', `LinkedIn_Processed_Guide_${this.selectedWorkshop?.name || 'unknown'}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.toastService.showSuccessMessage('Processed LinkedIn guide downloaded successfully.');
    });
  }

  viewUpdatedCv(): void {
    const url = this.selectedWorkshop?.processedLinkedInFile;
    if (!url) return;
    this.mediaService.getMediaUrl(url).subscribe((fullUrl) => {
      if (fullUrl) window.open(fullUrl, '_blank');
    });
  }

  private markDoneWithoutFile(): void {
    if (!this.selectedWorkshop) return;

    this.isWorking = true;
    const formData = new FormData();
    formData.append('ID', this.selectedWorkshop.packageID);
    formData.append('UserID', this.selectedWorkshop.userID || '');

    this.packagesService.UpdateWorkshop(formData).subscribe({
      next: () => {
        this.toastService.showSuccessMessage('LinkedIn marked as done.');
        this.isWorking = false;
        this.cdr.detectChanges();
        this.activeModal.close(true);
      },
      error: (error) => {
        this.toastService.showError(error);
        this.isWorking = false;
        this.cdr.detectChanges();
      },
    });
  }

  proceedWithWorkshop(): void {
    if (!this.selectedWorkshop) return;

    if (this.canMarkDone()) {
      this.markDoneWithoutFile();
      return;
    }

    this.isWorking = true;
    this.packagesService.ProceedWithWorkshop(this.selectedWorkshop.packageID).subscribe({
      next: () => {
        this.toastService.showSuccessMessage('Workshop status updated successfully.');
        this.isWorking = false;
        this.cdr.detectChanges();
        this.activeModal.close(true);
      },
      error: (error) => {
        this.toastService.showError(error);
        this.isWorking = false;
        this.cdr.detectChanges();
      },
    });
  }

  proceedButtonLabel(): string {
    if (this.isReceived()) return 'Mark as done';
    if (this.isLegacyReviewed()) return 'Mark Completed';
    if (this.isInProgress()) return 'Mark as done';
    return 'Proceed to Next Step';
  }
}
